import Script from "next/script";
import React, { useEffect } from "react";
import { Toaster } from "react-hot-toast";
import BaseLayout from "~/components/base-layout";
import { Footer } from "~/components/footer";
import { HeadContent } from "~/components/head";
import { MemberSearchProvider } from "~/components/member/containers/search";
import { SearchPopover } from "~/components/member/search";
import { SideNavigationProvider } from "~/components/navigation/side-navigation.container";
import { Page } from "~/components/page";
import { identify } from "~/lib/identify";
import { renderSegmentSnippet } from "~/lib/renderSegmentSnippet";
import { ExperimentProvider } from "~/state-containers/experiment";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { FEATURE_FLAGS } from "../lib/constants";
import { AppLockedPrompt } from "./modals/app-locked-prompt";
import { ModalsProvider } from "./modals/container";
import { DesktopQRModal } from "./modals/desktop-qr-modal";
import { SideNavigation } from "./navigation/side-navigation";
import { useHandleClickCTA } from "./workout/misc";

const ModalWrapper = () => {
  const { deepLink } = useHandleClickCTA();
  return <DesktopQRModal deepLink={deepLink} />;
};

const AppIdentifyComponent = React.memo(() => {
  const { currentUser } = useCurrentUser();
  useEffect(() => {
    if (!currentUser?.id) return;
    const { id, email, givenName, familyName, membershipStatus } = currentUser;
    identify({ userId: id, email, givenName, familyName, membershipStatus });
  }, [currentUser]);

  return null;
});

const AppLayout = ({ children, ...props }) => {
  return (
    <BaseLayout {...props}>
      <HeadContent />
      <AppIdentifyComponent />
      {process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY ? (
        <Script
          id="segment"
          dangerouslySetInnerHTML={{
            __html: renderSegmentSnippet(
              process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY
            ),
          }}
        />
      ) : null}
      <Toaster
        position="top-center"
        toastOptions={{
          // Define default options
          className: "",
          duration: 5000,
          background: "#333",
          color: "#fff",
        }}
      />
      <ExperimentProvider>
        <SideNavigationProvider>
          <MemberSearchProvider>
            <ModalsProvider>
              <ModalWrapper />
              <AppLockedPrompt />
              <SearchPopover />
              <SideNavigation showAuth={FEATURE_FLAGS.AUTH} />
              <Page>{children}</Page>
              <div className="w-full xl:px-16">
                <Footer />
              </div>
            </ModalsProvider>
          </MemberSearchProvider>
        </SideNavigationProvider>
      </ExperimentProvider>
    </BaseLayout>
  );
};

export default AppLayout;
